import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom/client";
import { useForm, Controller, useWatch } from "react-hook-form";
import DataTable from "react-data-table-component";
import { customTableStyles } from "../../assets/CustomTableStyles";
import { get, post } from "../../services/api";
import { toast } from "react-toastify";
import Select from "react-select";
import { PdfGenerator } from "../../components/PdfGenerator/PdfGenerator";
import TransferChallanHtml from "./TransferChallanHtml";
import FullScreenLoader from "../../components/FullScreenLoader";

function StudentTransferIn() {
  const token = localStorage.getItem("token");
  const organizationId = localStorage.getItem("organizationId");
  const savedCenterId =
    localStorage.getItem("centerId") === "null"
      ? null
      : localStorage.getItem("centerId");

  const {
    register,
    formState: { errors, isSubmitting },
    control,
    handleSubmit,
    watch,
    setValue,
    trigger,
    reset,
    getValues,
  } = useForm({
    defaultValues: {},
  });

  const [centerId, setCenterId] = useState(savedCenterId || null);
  const [centersData, setCentersData] = useState([]);
  const [classesData, setClassesData] = useState([]);
  const [sections, setSections] = useState([]);
  const [selectedClassId, setSelectedClassId] = useState(null);
  const [selectedSectionId, setSelectedSectionId] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [isTransferChallan, setIsTransferChallan] = useState(false);

  const [transferredInStudentsData, setTransferredInStudentsData] = useState(
    []
  );

  const [triggerRerender, setTriggerRerender] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loading, setLoading] = useState(false);

  const [isModalVisible, setModalVisible] = useState(false);
  const showModal = () => setModalVisible(true);
  const hideModal = () => {
    setModalVisible(false);
    setSelectedRow(null);

    reset({
      centerId: getValues("centerId"),
      centerClassId: "",
      sectionId: "",
      studentTransferId: "",
      challanNo: "",
      isTransfer: false,
      createdBy: localStorage.getItem("userId"),
    });
  };

  const [isGenerateChallanModalVisible, setIsGenerateChallanModalVisible] =
    useState(false);
  const showGenerateChallanModal = () => setIsGenerateChallanModalVisible(true);
  const hideGenerateChallanModal = () => {
    setIsGenerateChallanModalVisible(false);
    setSelectedRow(null);

    reset({
      challanDate: "",
      dueDate: "",
    });
  };

  useEffect(() => {
    const fetchCenters = async () => {
      try {
        const response = await get(
          `Dropdown/GetAllCentersByOrganizationId?organizationId=${organizationId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        setCentersData(response);

        if (savedCenterId) {
          const selectedCenter = response.find(
            (center) => center.id === parseInt(savedCenterId, 10)
          );

          if (selectedCenter) {
            setValue("centerId", {
              value: selectedCenter.id,
              label: selectedCenter.name,
            });

            fetchClassesByCenterId(savedCenterId);
            fetchTransferredInStudents(savedCenterId);
          }
        }
      } catch (error) {
        console.error("Error fetching centers:", error);
      }
    };

    fetchCenters();
  }, []);

  const fetchClassesByCenterId = async (centerId) => {
    try {
      if (centerId) {
        const response = await get(
          `CenterClass/GetClassCenterByCenterId?id=${centerId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        setClassesData(response);
      } else {
        setClassesData([]);
      }
    } catch (error) {
      console.error("Error fetching classes:", error);
    }
  };

  const getAllClassSectionsByClassIdAndCenterId = async (classId, centerId) => {
    try {
      if (classId && centerId) {
        const response = await get(
          `ClassSection/GetAllSectionByCenterClassIdAndCenterId?centerClassId=${classId}&centerId=${centerId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        setSections(response);
      } else {
        setSections([]);
      }
    } catch (error) {
      console.error("Error fetching sections:", error);
    }
  };

  useEffect(() => {
    if (centerId && selectedClassId) {
      getAllClassSectionsByClassIdAndCenterId(selectedClassId, centerId);
    }
  }, [centerId, selectedClassId, triggerRerender]);

  const fetchTransferredInStudents = async (centerId) => {
    try {
      const response = await get(
        `Student/GetStudentTransferInList?centerId=${centerId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setTransferredInStudentsData(response?.studentTransferList || []);
    } catch (error) {
      console.error("Error fetching students:", error);
    }
  };

  const columns = [
    {
      name: "Sr No.",
      cell: (row, index) => (currentPage - 1) * rowsPerPage + index + 1,
      sortable: false,
      width: "60px",
    },
    {
      name: "Pupil",
      selector: (row) => row.studentName,
      sortable: true,
      width: "150px",
    },
    {
      name: "Trasferred From",
      selector: (row) => row.fromCenter,
      sortable: true,
      width: "150px",
    },
    {
      name: "Trasferred To",
      selector: (row) => row.toCenter,
      sortable: true,
      width: "150px",
    },
    {
      name: "Trasfer Date",
      selector: (row) => row.transferDate.split("T")[0],
      sortable: true,
      width: "120px",
    },
    {
      name: "Challan No.",
      selector: (row) => row.challanId,
      sortable: true,
      width: "120px",
    },
    {
      name: "Approved",
      selector: (row) => (row.isApproved ? "Yes" : "No"),
      sortable: true,
    },
    {
      name: "Paid",
      selector: (row) => (row.isPaid ? "Yes" : "No"),
      sortable: true,
    },
    {
      name: "Challan Actions",
      width: "200px",
      selector: (row) => (
        <div className="d-flex gap-1">
          {/* Approve Button */}
          {!row.isApproved && (
            <button
              type="button"
              className="btn btn-outline-primary py-1 px-2"
              onClick={() => handleApproveClick(row, true)}
            >
              Approve
            </button>
          )}

          {/* Reject Button */}
          {/* {!row.isApproved && ( */}
          <button
            type="button"
            className="btn btn-primary py-1 px-2"
            onClick={() => handleApproveClick(row, false)}
          >
            Reject
          </button>
          {/* )} */}

          {/* Generate Challan Button */}
          {!row.challanId && row.isApproved && (
            <button
              type="button"
              className="btn btn-outline-primary py-1 px-2"
              onClick={() => handleGenerateChallanClick(row)}
            >
              Generate
            </button>
          )}

          {/* Pay Challan Button */}
          {row.challanId && !row.isPaid && (
            <button
              type="button"
              className="btn btn-outline-primary py-1 px-2"
              onClick={() => handlePayClick(row, false)}
            >
              Pay
            </button>
          )}

          {/* Print Challan Button */}
          {row.challanId && (
            <button
              type="button"
              className="btn btn-primary py-1 px-2"
              onClick={() => handlePrintClick(row.challanId)}
            >
              Print
            </button>
          )}
        </div>
      ),
    },
  ];

  const handleApproveClick = async (row, isApproved) => {
    const confirmationMessage = isApproved
      ? "Are you sure you want to approve this student transfer?"
      : "Are you sure you want to reject this student transfer?";

    if (window.confirm(confirmationMessage)) {
      const formData = {
        isApproved,
        studentTransferId: row.studentTransferId,
        createdBy: localStorage.getItem("userId"),
      };

      await submitApprovalStatus(formData);
    }
  };

  const submitApprovalStatus = async (data) => {
    if (!token) {
      console.error("Unauthorized.");
      return;
    }

    try {
      const response = await post(`Student/ApproveRejectStudent`, data, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.isSuccess) {
        toast.success(response.message || "Success!");
        setTriggerRerender((prev) => !prev);
        fetchTransferredInStudents(centerId);
      } else {
        toast.error(response.message || "Error!");
      }
    } catch (error) {
      console.error("Error saving:", error);
    }
  };

  const saveData = async (data) => {
    if (!token) {
      console.error("Unauthorized.");
      return;
    }

    const formdata = {
      centerId: data?.centerId?.value,
      classSectionId: data?.classSectionId?.value,
      studentId: data?.studentId?.value,
      toCenterId: data?.toCenterId?.value,
      createdBy: localStorage.getItem("userId"),
    };

    try {
      const response = await post(`Student/AddStudentTransferOut`, formdata, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.isSuccess) {
        toast.success(response.message || "Success!");
        setTriggerRerender((prev) => !prev);
        fetchTransferredInStudents(centerId);
        reset({
          id: 0,
          centerId: getValues("centerId"),
          classSectionId: "",
          studentId: "",
          toCenterId: "",
          createdBy: localStorage.getItem("userId"),
        });
      }
    } catch (error) {
      console.error("Error saving category:", error);
    }
  };

  const handleGenerateChallanClick = async (row) => {
    setIsGenerateChallanModalVisible(true);
    setSelectedRow(row);
    setValue("challanDate", new Date().toISOString().split("T")[0]);
  };

  const submitGenerateChallan = async () => {
    if (!token) {
      console.error("Unauthorized.");
      return;
    }

    const isValid = await trigger(["challanDate", "dueDate"]);
    if (!isValid) {
      return;
    }

    const challanDate = getValues("challanDate");
    const dueDate = getValues("dueDate");

    const formData = {
      challanDate,
      dueDate,
      studentId: selectedRow.studentId,
      createdBy: localStorage.getItem("userId"),
    };

    try {
      const response = await post(
        `Student/GenerateSecurityDepositChallan`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.isSuccess) {
        toast.success(response.message || "Success!");
        setTriggerRerender((prev) => !prev);
        hideGenerateChallanModal();
        fetchTransferredInStudents(centerId);
      } else {
        toast.error(response.message || "Error!");
      }
    } catch (error) {
      console.error("Error saving:", error);
    }
  };

  const fetchChallanData = async (regNo) => {
    setLoading(true);
    try {
      const response = await get(
        `Student/PrintTransferSecurityChallan?ChallanNo=${regNo}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.isSuccess) {
        return response?.transferSecurityChallan;
      } else {
        throw new Error("Failed to fetch challan data.");
      }
    } catch (error) {
      console.error("Error fetching challan data:", error);
      throw error;
    }
  };

  const handlePrintClick = async (challanId) => {
    if (!challanId) {
      alert("Please select challan.");
      return;
    }

    setLoading(true);

    try {
      const challanData = await fetchChallanData(challanId);

      const container = document.createElement("div");
      container.style.position = "absolute";
      container.style.left = "-9999px";
      document.body.appendChild(container);

      const root = ReactDOM.createRoot(container);
      root.render(
        <div>
          <TransferChallanHtml challanData={challanData} />
        </div>
      );

      await new Promise((resolve) => setTimeout(resolve, 200));

      await PdfGenerator(container);

      root.unmount();
      document.body.removeChild(container);
    } catch (error) {
      console.error("Error generating PDF:", error);
      alert("Failed to generate PDF. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handlePayClick = async (row, payType) => {
    setModalVisible(true);
    setSelectedRow(row);
    setIsTransferChallan(payType);
  };

  const submitPayChallan = async (data) => {
    if (!token) {
      console.error("Unauthorized.");
      return;
    }

    const formData = {
      centerId: data?.centerId?.value,
      centerClassId: data?.centerClassId?.value,
      sectionId: data?.sectionId?.value,
      studentTransferId: selectedRow.studentTransferId,
      challanNo: selectedRow.challanId,
      isTransfer: isTransferChallan,
      createdBy: localStorage.getItem("userId"),
    };

    // console.log(formData);
    // return;

    try {
      const response = await post(`Student/PayTransferChallan`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.isSuccess) {
        toast.success(response.message || "Success!");
        setTriggerRerender((prev) => !prev);
        hideModal();
        fetchTransferredInStudents(centerId);
      } else {
        toast.error(response.message || "Error!");
      }
    } catch (error) {
      console.error("Error saving:", error);
    }
  };

  return (
    <>
      <div className="page-header d-flex mb-2">
        <h2 className="page-heading fs-7">Pupil Transfer</h2>
      </div>

      <div className="row g-3">
        <div className="col-md-12">
          <div className="card">
            <div className="card-body">
              <h5 className="mb-2">Pupils Transferred In</h5>
              <div className="table-container fixed-last-column-table">
                <DataTable
                  columns={columns}
                  data={transferredInStudentsData}
                  pagination
                  dense
                  customStyles={customTableStyles}
                  onChangePage={setCurrentPage}
                  onChangeRowsPerPage={setRowsPerPage}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {isModalVisible && (
        <>
          <div
            className="modal fade show"
            tabIndex="-1"
            style={{ display: "block" }}
          >
            <div className="modal-dialog">
              <div className="modal-content">
                <form onSubmit={handleSubmit(submitPayChallan)}>
                  <div className="modal-header">
                    <h5 className="mb-0">Pay Security Deposit Challan</h5>
                    <button
                      type="button"
                      className="btn-close"
                      onClick={hideModal}
                    ></button>
                  </div>
                  <div className="modal-body">
                    <div className="row gx-2 gy-3">
                      <div className="col-md-6">
                        <label className="form-label">
                          Center
                          <span className="text-danger fs-9">*</span>
                        </label>
                        <Controller
                          name="centerId"
                          control={control}
                          rules={{ required: "This field is required." }}
                          render={({ field }) => (
                            <Select
                              {...field}
                              options={centersData.map((center) => ({
                                value: center.id,
                                label: center.name,
                              }))}
                              isDisabled={savedCenterId !== "0"}
                              onChange={(selectedOption, actionMeta) => {
                                if (actionMeta.action === "clear") {
                                  setValue("centerId", null);
                                } else {
                                  setValue("centerId", selectedOption);
                                  setCenterId(selectedOption.value);
                                  fetchClassesByCenterId(selectedOption.value);
                                }
                                setClassesData([]);
                                setValue("centerClassId", null);
                                setSelectedClassId(null);
                                trigger("centerId");
                              }}
                              className="react-select-style"
                              classNamePrefix="react-select-style-pref"
                              // isClearable
                            />
                          )}
                        />
                        {errors.centerId && (
                          <small className="text-danger">
                            {errors.centerId.message}
                          </small>
                        )}
                      </div>
                      <div className="col-md-6">
                        <label className="form-label">
                          Pupil
                          <span className="text-danger fs-9">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          value={selectedRow.studentName}
                          disabled
                        />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label">
                          Class<span className="text-danger fs-9">*</span>
                        </label>
                        <Controller
                          name="centerClassId"
                          control={control}
                          rules={{ required: "This field is required." }}
                          render={({ field }) => (
                            <Select
                              {...field}
                              options={classesData.map((classItem) => ({
                                value: classItem.id,
                                label: classItem.name,
                              }))}
                              onChange={(selectedOption, actionMeta) => {
                                if (actionMeta.action === "clear") {
                                  setValue("centerClassId", null);
                                  setSelectedClassId(null);
                                  setSections([]);
                                } else {
                                  setValue("centerClassId", selectedOption);
                                  setSelectedClassId(selectedOption.value);
                                }
                                trigger("centerClassId");
                              }}
                              className="react-select-style"
                              classNamePrefix="react-select-style-pref"
                              isClearable
                            />
                          )}
                        />
                        {errors.centerClassId && (
                          <small className="text-danger">
                            {errors.centerClassId.message}
                          </small>
                        )}
                      </div>
                      <div className="col-md-6">
                        <label className="form-label">
                          Section<span className="text-danger fs-9">*</span>
                        </label>
                        <Controller
                          name="sectionId"
                          control={control}
                          rules={{ required: "This field is required." }}
                          render={({ field }) => (
                            <Select
                              {...field}
                              options={sections.map((sectionItem) => ({
                                value: sectionItem.id,
                                label: sectionItem.name,
                              }))}
                              onChange={(selectedOption, actionMeta) => {
                                if (actionMeta.action === "clear") {
                                  setValue("sectionId", null);
                                  setSelectedSectionId(null);
                                  setSections([]);
                                } else {
                                  setValue("sectionId", selectedOption);
                                  setSelectedSectionId(selectedOption.value);
                                }
                                trigger("sectionId");
                              }}
                              className="react-select-style"
                              classNamePrefix="react-select-style-pref"
                              isClearable
                            />
                          )}
                        />
                        {errors.sectionId && (
                          <small className="text-danger">
                            {errors.sectionId.message}
                          </small>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button type="submit" className="btn btn-sm btn-primary">
                      Pay
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-primary"
                      onClick={hideModal}
                    >
                      Close
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show"></div>
        </>
      )}

      {isGenerateChallanModalVisible && (
        <>
          <div
            className="modal fade show"
            tabIndex="-1"
            style={{ display: "block" }}
          >
            <div className="modal-dialog">
              <div className="modal-content">
                <form>
                  <div className="modal-header">
                    <h5 className="mb-0">Generate Security Deposit Challan</h5>
                    <button
                      type="button"
                      className="btn-close"
                      onClick={hideGenerateChallanModal}
                    ></button>
                  </div>
                  <div className="modal-body">
                    <div className="row gx-2 gy-3">
                      <div className="col-md-6">
                        <label className="form-label">
                          Challan Date
                          <span className="text-danger fs-9">*</span>
                        </label>
                        <input
                          {...register(`challanDate`, {
                            required: "This field is required.",
                          })}
                          type="date"
                          className="form-control"
                          disabled={true}
                        />
                        {errors.challanDate && (
                          <small className="text-danger">
                            {errors.challanDate.message}
                          </small>
                        )}
                      </div>
                      <div className="col-md-6">
                        <label className="form-label">
                          Due Date
                          <span className="text-danger fs-9">*</span>
                        </label>
                        <input
                          {...register(`dueDate`, {
                            required: "This field is required.",
                            validate: {
                              futureDate: (value) => {
                                const selectedDate = new Date(value);
                                const today = new Date();
                                today.setHours(0, 0, 0, 0);
                                return (
                                  selectedDate >= today ||
                                  "Please select a valid date."
                                );
                              },
                            },
                          })}
                          type="date"
                          className="form-control"
                        />
                        {errors.dueDate && (
                          <small className="text-danger">
                            {errors.dueDate.message}
                          </small>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button
                      type="button"
                      className="btn btn-sm btn-primary"
                      onClick={submitGenerateChallan}
                    >
                      Save
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-primary"
                      onClick={hideGenerateChallanModal}
                    >
                      Close
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show"></div>
        </>
      )}

      {loading && <FullScreenLoader />}
    </>
  );
}

export default StudentTransferIn;
